import React, { useState, useEffect, useRef } from "react";
import InstallConfigureSteps from "./InstallConfigureSteps.jsx";
import { wsRequest } from "./wsClient.js";
import { announceAuthChange } from "./authPrompt.js";
import Footer from "./Footer.jsx";
import { useNavigate } from "./Navigation.jsx";
import TotpSetup from "./TotpSetup.jsx";

const USER_TOKEN_KEY = "589comps_user_token";
// AdminApp.jsx reads its own session from this specific, separate
// key on mount — entirely independent of the main site's key above.
// A root account is functionally an admin-panel-only account, so
// without also writing here, "Go to admin area" would land on a
// blank session and force a fresh login, even though the account was
// already fully logged in moments earlier.
const ADMIN_TOKEN_KEY = "589comps_admin_token";

// A simple, self-contained CSS-based particle effect — no new
// dependency for a one-off celebratory touch. A handful of colored
// dots animate outward from a few origin points and fade, using CSS
// keyframes rather than a canvas/animation library.
function Fireworks() {
  const colors = ["#ff5e5e", "#ffd93d", "#6bcB77", "#4d96ff", "#c77dff"];
  const bursts = [
    { left: "20%", top: "30%" },
    { left: "50%", top: "20%" },
    { left: "80%", top: "35%" },
    { left: "35%", top: "55%" },
    { left: "65%", top: "50%" },
  ];
  const particlesPerBurst = 12;

  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 50 }}>
      <style>{`
        @keyframes firework-particle {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) scale(0.3); opacity: 0; }
        }
      `}</style>
      {bursts.map((burst, burstIndex) =>
        Array.from({ length: particlesPerBurst }).map((_, i) => {
          const angle = (i / particlesPerBurst) * 2 * Math.PI;
          const distance = 60 + Math.random() * 60;
          const dx = `${Math.cos(angle) * distance}px`;
          const dy = `${Math.sin(angle) * distance}px`;
          return (
            <div
              key={`${burstIndex}-${i}`}
              style={{
                position: "absolute",
                left: burst.left,
                top: burst.top,
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: colors[(burstIndex + i) % colors.length],
                "--dx": dx,
                "--dy": dy,
                animation: `firework-particle 1.1s ease-out ${burstIndex * 0.25}s forwards`,
              }}
            />
          );
        })
      )}
    </div>
  );
}

// ---- THE DOMAIN FIELD IS PREFILLED, AND WHICH SOURCE WINS MATTERS (1.1042.0) ----
//
// WHAT THIS FIELD COSTS WHEN IT IS WRONG. It builds the links in every email
// the site sends. Unset, siteUrlForEmail returns an EMPTY STRING, so a reset
// link and an unsubscribe link come out BLANK rather than wrong. It carries
// `required` below, so the normal path already cannot leave it empty - what a
// prefill closes is the TYPO, by offering a correct default instead of an
// empty box.
//
// THE BROWSER'S OWN ADDRESS WINS, and that is the whole point rather than a
// convenience: an address that loaded this page cannot be a typo. The
// installer's answer is only shape-checked - its v_domain accepts any
// well-formed domain, and install.sh says in terms that the DNS is ASSUMED to
// point at the box - so prefilling from that first would carry a typo forward
// with a confident-looking default behind it. It also gets http vs https right
// for free, by being how this page was actually reached.
//
// SUPPRESSED ON A BARE IP, which is the one case the browser cannot answer.
// An IP here would be written into every email link, so the installer's domain
// is used instead; with neither, the field is left empty exactly as it was
// before this existed, rather than filled with something wrong.
export function prefillSiteUrl(installDomain, loc) {
  const host = (loc && loc.hostname) || "";
  // AN IPv6 HOST ARRIVES WITHOUT ITS BRACKETS and may still contain a dot -
  // `::ffff:1.2.3.4` has both - so the colon test is not covered by the dot
  // test and both are needed.
  const isIpv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
  const isIpv6 = host.includes(":");
  if (host.includes(".") && !isIpv4 && !isIpv6) return (loc && loc.origin) || "";
  if (typeof installDomain === "string" && installDomain.trim()) {
    return `https://${installDomain.trim()}`;
  }
  return "";
}

export default function InstallPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null); // null (loading) | "needed" | "already-installed"
  const [siteName, setSiteName] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [fogLine1, setFogLine1] = useState("");
  const [fogLine2, setFogLine2] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Feeds the browser's own native validity state, via
  // setCustomValidity — HTML5's built-in required/minLength validation
  // has no concept of "must match another field" on its own, so this is
  // the one piece that needs real JS rather than just the required
  // attribute. Setting a non-empty string marks the field :invalid
  // (integrating with theme.css's red/green border styling the exact
  // same way a plain required/minLength failure would); an empty string
  // clears it back to :valid.
  const confirmPasswordRef = useRef(null);
  useEffect(() => {
    if (confirmPasswordRef.current) {
      confirmPasswordRef.current.setCustomValidity(confirmPassword && confirmPassword !== password ? "Passwords don't match." : "");
    }
  }, [password, confirmPassword]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Holds the just-created root account + its session token during
  // the mandatory 2FA step below — deliberately NOT yet persisted to
  // localStorage or announced via announceAuthChange at this point.
  // The token is still fully valid for the auth.totp.* calls
  // TotpSetup itself makes (those only need a real, authenticated
  // session — not specifically one already stored globally), so this
  // works without treating the account as "properly logged in"
  // anywhere else yet, consistent with 2FA being mandatory before
  // that's true for any admin/root account.
  const [installedRoot, setInstalledRoot] = useState(null); // { user, token } | null
  // THE SESSION THE CONFIGURATION STEPS RUN ON.
  //
  // Kept separately because finishAfterTotp clears installedRoot — and the
  // steps below read their token from it, so they were handed `undefined`
  // and every one said "Login required". The install completed, a token
  // existed, and nothing could be saved with it.
  //
  // Reported 7 Sep, on the first fresh install anybody had walked through.
  const [sessionToken, setSessionToken] = useState(null);
  const [result, setResult] = useState(null); // { user } | null — only ever set once 2FA is genuinely done
  // Whether the optional configuration steps have been worked through or
  // dismissed. Not persisted: they are offered once, on this screen, and
  // everything in them lives in the admin panel afterwards.
  const [configureDone, setConfigureDone] = useState(false);

  useEffect(() => {
    wsRequest("install.status", {})
      .then((data) => {
        setStatus(data.needsInstall ? "needed" : "already-installed");
        // NEVER OVER AN ANSWER ALREADY TYPED. The form does not render until
        // status is set, so today nothing can have been typed first - the
        // functional form is here so that a second status read, if one is ever
        // added, cannot overwrite the operator's own answer.
        if (data.needsInstall) {
          setSiteUrl((current) => current || prefillSiteUrl(data.installDomain, window.location));
        }
      })
      .catch(() => setStatus("already-installed")); // fail closed — never show the install form on an unclear/errored check
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const data = await wsRequest("install.complete", {
        siteName,
        siteUrl,
        fogLine1,
        fogLine2,
        email,
        password,
      });
      setInstalledRoot({ user: data.user, token: data.token });
    } catch (err) {
      setError(err.message || "Could not reach backend.");
    }
    setBusy(false);
  };

  // Only now — once 2FA is genuinely set up — does the account
  // actually become "logged in" anywhere else: persisted to
  // localStorage and announced globally, same as every other real
  // login flow in this app. Deliberately re-fetches the user via
  // auth.me rather than reusing installedRoot.user directly — that
  // snapshot was captured by install.complete BEFORE 2FA was set up,
  // so it still shows totpEnabled: false. Announcing that stale
  // object would make the admin panel think 2FA still needs setup
  // and show the mandatory interstitial a second time, immediately
  // after it was just completed here.
  const finishAfterTotp = async () => {
    localStorage.setItem(USER_TOKEN_KEY, installedRoot.token);
    localStorage.setItem(ADMIN_TOKEN_KEY, installedRoot.token);
    let freshUser = installedRoot.user;
    try {
      freshUser = await wsRequest("auth.me", {}, installedRoot.token);
    } catch {
      // Falls back to the stale snapshot if this somehow fails — still
      // logged in either way, just a rare chance of the interstitial
      // showing once more, not a hard failure of the install itself.
    }
    announceAuthChange(freshUser, installedRoot.token);
    setSessionToken(installedRoot.token);
    setResult({ user: freshUser });
    setInstalledRoot(null);
  };

  if (installedRoot) {
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
        <div style={{ maxWidth: 480, margin: "0 auto", padding: "40px 20px" }}>
          <h2>Set up two-factor authentication</h2>
          <p style={{ opacity: 0.85, lineHeight: 1.6 }}>
            Required for the root account before you can continue — protects the admin area even if the password is
            ever compromised.
          </p>
          <TotpSetup token={installedRoot.token} onComplete={finishAfterTotp} />
        </div>
        <Footer />
      </div>
    );
  }

  if (status === null) {
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh", padding: "40px 20px" }}>
        <p>Loading…</p>
      </div>
    );
  }

  if (status === "already-installed") {
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
        <div style={{ maxWidth: 480, margin: "0 auto", padding: "40px 20px" }}>
          <h2>Already installed</h2>
          <p style={{ opacity: 0.85, lineHeight: 1.6 }}>
            This site has already been installed. If you need to start over, that has to be done from the server
            itself, via the install script — not from this page.
          </p>
          <button onClick={() => { window.location.href = "/"; }}>Go to site</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (result) {
    // CONFIGURATION COMES AFTER THE CELEBRATION, not before it.
    //
    // The install itself is complete and the site is usable at this
    // point — root exists, 2FA is set up, and needsInstall is already
    // false. So nothing below can lock anybody out, every step is
    // skippable, and leaving at any moment is fine.
    //
    // It sits here rather than earlier because these steps need an
    // authenticated session to save anything, and that session only
    // exists once 2FA is done.
    if (!configureDone) {
      return (
        <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
          <div style={{ maxWidth: 620, margin: "0 auto", padding: "40px 20px" }}>
            <h2>Installed — now let's set it up</h2>
            <InstallConfigureSteps
              token={sessionToken}
              wsRequest={wsRequest}
              onFinish={() => setConfigureDone(true)}
            />
          </div>
        </div>
      );
    }
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
        <Fireworks />
        <div style={{ maxWidth: 480, margin: "0 auto", padding: "40px 20px", position: "relative", zIndex: 10 }}>
          <h2>🎉 Congratulations — installation was successful!</h2>
          <p style={{ opacity: 0.85, lineHeight: 1.6 }}>
            Your site is ready. The admin area is accessible using the root username and password you just created —
            two-factor authentication is already set up, so you'll go straight in.
          </p>
          <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Homepage:</strong> <a href="/">{window.location.origin}/</a>
            </div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>Admin area:</strong> <a href="/admin">{window.location.origin}/admin</a>
            </div>
            <div style={{ fontSize: 13 }}>
              <strong>Root login:</strong> {result.user.email}
            </div>
          </div>
          <button onClick={() => { window.location.href = "/admin"; }}>Go to admin area</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
      <div style={{ maxWidth: 480, margin: "0 auto", padding: "40px 20px" }}>
        <h2>Set up your site</h2>
        <form onSubmit={submit}>
          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Site name</label>
          <input
            type="text"
            required
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", fontSize: 14, marginBottom: 12, boxSizing: "border-box" }}
          />

          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            Domain
            <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2 }}>
              Include https://, no trailing slash — used to build links in emails, and as the domain for demo
              account addresses below.
            </div>
          </label>
          <input
            type="text"
            required
            placeholder="https://example.com"
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", fontSize: 14, marginBottom: 12, boxSizing: "border-box" }}
          />

          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            Cover text (optional)
            <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2 }}>
              Two lines of text shown over every competition before any purchases are made. Can be set up later
              from Sitewide settings instead — leave blank for a plain dark cover with no text.
            </div>
          </label>
          <input
            type="text"
            placeholder="ACME Competitions"
            value={fogLine1}
            onChange={(e) => setFogLine1(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", fontSize: 14, marginBottom: 8, boxSizing: "border-box" }}
          />
          <input
            type="text"
            placeholder="Select an area here"
            value={fogLine2}
            onChange={(e) => setFogLine2(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", fontSize: 14, marginBottom: 12, boxSizing: "border-box" }}
          />

          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Root account email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", fontSize: 14, marginBottom: 12, boxSizing: "border-box" }}
          />

          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Root account password</label>
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", fontSize: 14, marginBottom: 12, boxSizing: "border-box" }}
          />

          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>Confirm password</label>
          <input
            ref={confirmPasswordRef}
            type="password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", fontSize: 14, marginBottom: 20, boxSizing: "border-box" }}
          />


          <button type="submit" disabled={busy}>
            {busy ? "Installing…" : "Start Install"}
          </button>
          {error && <div style={{ color: "var(--accent-red)", fontSize: 13, marginTop: 8 }}>{error}</div>}
        </form>
      </div>
      <Footer />
    </div>
  );
}
